import { DataItem } from 'scrivito'

import { EnumValue } from '@/Data/typesV2'

export const formatTimerecordDate = (value: unknown): string => {
  if (!value) return ''
  const date = new Date(value as string)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

export const ensureNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return value
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Gets the display title for a status value from schema
 * @param statusValue - The status value from data item
 * @param statusOptions - Array of available status enum values
 * @returns The title from schema or the original value if not found
 */
export const getStatusTitle = (
  statusValue: string,
  statusOptions?: EnumValue[],
): string | null => {
  if (!statusValue || !statusOptions || statusOptions.length === 0) {
    return statusValue
  }

  const enumValue = statusOptions.find((e) => e.value === statusValue)

  return enumValue?.title || statusValue
}

/**
 * Maps status value to badge colour
 * @param status - Status value (e.g., 'open', 'checked', 'released')
 * @returns Badge colour string
 */
export const getStatusColour = (status: string | undefined): string => {
  const statusLower = status?.toLowerCase()

  if (statusLower === 'open') {
    return 'grey'
  }
  if (statusLower === 'checked') {
    return 'blue'
  }
  if (statusLower === 'released') {
    return 'green'
  }

  // Default to grey
  return 'grey'
}

export interface TimerecordDurations {
  totalDuration: number
  billableDuration: number
  nonBillableDuration: number
}

/**
 * Extracts duration values from a timerecord data item, taking into account
 * whether the record is marked as billable. Missing or invalid values
 * default to 0.
 */
export const getTimerecordDurations = (
  dataItem: DataItem,
): TimerecordDurations => {
  const isBillable = dataItem.get('billable') === true
  const totalDuration = ensureNumber(dataItem.get('totalDuration')) ?? 0
  const rawBillableDuration = ensureNumber(dataItem.get('billedDuration')) ?? 0

  const billableDuration = isBillable ? rawBillableDuration : 0
  const nonBillableDuration = isBillable
    ? Math.max(0, totalDuration - rawBillableDuration)
    : totalDuration

  return { totalDuration, billableDuration, nonBillableDuration }
}

/**
 * Formats duration in hours (decimal) to "Xh Ym" format
 * @param hours - Duration in hours (can be decimal, e.g., 1.5 = 1h30min)
 * @returns Formatted string like "23h" or "11h30"
 */
export const formatDuration = (hours: number | null): string => {
  if (hours === null || hours === 0) {
    return '0h'
  }

  const totalMinutes = Math.round(hours * 60)
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60

  if (m === 0) {
    return `${h}h`
  }
  return `${h}h${m}`
}

export interface TimerecordTotals {
  totalDuration: number
  billableDuration: number
  nonBillable: number
}

/**
 * Calculates totals for timerecord data items
 * @param dataItems - Array of timerecord data items
 * @returns Object with totalDuration, billableDuration, nonBillable, and missing hours
 */
export const calculateTimerecordTotals = (
  dataItems: DataItem[],
): TimerecordTotals => {
  return dataItems.reduce(
    (acc, dataItem) => {
      const { totalDuration, billableDuration, nonBillableDuration } =
        getTimerecordDurations(dataItem)

      return {
        totalDuration: acc.totalDuration + totalDuration,
        billableDuration: acc.billableDuration + billableDuration,
        nonBillable: acc.nonBillable + nonBillableDuration,
      }
    },
    { totalDuration: 0, billableDuration: 0, nonBillable: 0 },
  )
}
